import React, { useState, useEffect, useCallback } from 'react';
import { reportingApi } from '../services/reportingApi';
import { ScrollText, Filter, Download, RefreshCw, User, AlertCircle, ChevronDown, RotateCcw, Calendar } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const ACTION_TYPES = [
  { key: '', label: 'Tous les types' },
  { key: 'manual', label: 'Correction manuelle' },
  { key: 'auto', label: 'Correction automatique' },
  { key: 'direct_edit', label: 'Édition directe' },
  { key: 'revert', label: 'Annulation correction' },
  { key: 'production', label: 'Production reporting' },
  { key: 'upload', label: 'Upload fichier Excel' },
];

const ACTION_COLORS = {
  manual: 'bg-blue-100 text-blue-800 border-blue-200',
  auto: 'bg-purple-100 text-purple-800 border-purple-200',
  direct_edit: 'bg-amber-100 text-amber-800 border-amber-200',
  revert: 'bg-red-100 text-red-800 border-red-200',
  production: 'bg-green-100 text-green-800 border-green-200',
  upload: 'bg-teal-100 text-teal-800 border-teal-200',
};

const STAT_LABELS = {
  manual: 'Corrections man.',
  auto: 'Corrections auto.',
  direct_edit: 'Éditions directes',
  revert: 'Annulations',
  production: 'Productions',
  upload: 'Uploads',
};

const ActionBadge = ({ actionKey, label }) => {
  const cls = ACTION_COLORS[actionKey] || 'bg-slate-100 text-slate-700 border-slate-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded border ${cls}`}>
      {label}
    </span>
  );
};

const formatTs = (ts) => {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return ts;
  }
};

const exportCsv = (events) => {
  const headers = ['Date/Heure', 'Utilisateur', "Type d'action", 'Module', 'Référence', 'Détails', 'Annulée'];
  const rows = events.map((e) => [
    e.timestamp ? new Date(e.timestamp).toLocaleString('fr-FR') : '',
    e.user,
    e.action_type,
    e.module,
    e.entity_ref,
    e.details,
    e.reverted ? 'Oui' : 'Non',
  ]);
  const csv = [headers, ...rows]
    .map((r) => r.map((c) => `"${String(c || '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-reporting-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─────────────────────────────────────────────────────────────────────────────
// Page component
// ─────────────────────────────────────────────────────────────────────────────

const AuditLogPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const [filters, setFilters] = useState({
    from_date: '',
    to_date: '',
    user: '',
    action_type: '',
    closing_date: '',
  });
  const [activeFilters, setActiveFilters] = useState({});

  const fetchLogs = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const clean = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== '' && v != null)
      );
      const res = await reportingApi.getAuditLogs(clean);
      setEvents(res.data.events || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Erreur lors du chargement');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleApply = () => {
    setActiveFilters({ ...filters });
    fetchLogs(filters);
  };

  const handleReset = () => {
    const empty = { from_date: '', to_date: '', user: '', action_type: '', closing_date: '' };
    setFilters(empty);
    setActiveFilters({});
    fetchLogs({});
  };

  const [revertLoading, setRevertLoading] = useState(null);
  const [revertMsg, setRevertMsg] = useState(null);

  const handleRevert = async (logId) => {
    setRevertLoading(logId);
    try {
      const res = await reportingApi.cidRevert(logId);
      setRevertMsg({ type: 'success', text: res.data?.message || 'Correction annulée.' });
      setTimeout(() => setRevertMsg(null), 5000);
      fetchLogs(activeFilters);
    } catch (err) {
      setRevertMsg({ type: 'error', text: err.response?.data?.detail || 'Erreur lors de l\'annulation.' });
    } finally {
      setRevertLoading(null);
    }
  };

  const stats = events.reduce((acc, e) => {
    acc[e.action_key] = (acc[e.action_key] || 0) + 1;
    return acc;
  }, {});

  const hasActiveFilters = Object.values(activeFilters).some((v) => v !== '');

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded bg-[#004A99]/10 shrink-0">
            <ScrollText size={18} className="text-[#004A99]" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-slate-800">Journal d'audit — Reporting</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Corrections CID (manuelles, automatiques, éditions directes, annulations) · Productions · Uploads
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => fetchLogs(activeFilters)}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 rounded transition-colors"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
          <button
            onClick={() => exportCsv(events)}
            disabled={events.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white bg-[#004A99] hover:bg-[#003d80] rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download size={12} />
            Exporter CSV ({events.length})
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded p-4">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-3">
          <Filter size={12} />
          Filtres
          {hasActiveFilters && (
            <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-[#004A99] text-white rounded-full">actifs</span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Date début</label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))}
              className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#004A99] bg-slate-50"
            />
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Date fin</label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))}
              className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#004A99] bg-slate-50"
            />
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Date d'arrêté</label>
            <div className="relative">
              <Calendar size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="DD/MM/YY"
                value={filters.closing_date}
                onChange={(e) => setFilters((f) => ({ ...f, closing_date: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && handleApply()}
                className="w-full text-xs border border-slate-200 rounded pl-6 pr-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#004A99] bg-slate-50"
              />
            </div>
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Utilisateur</label>
            <div className="relative">
              <User size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Login..."
                value={filters.user}
                onChange={(e) => setFilters((f) => ({ ...f, user: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && handleApply()}
                className="w-full text-xs border border-slate-200 rounded pl-6 pr-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#004A99] bg-slate-50"
              />
            </div>
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Type d'action</label>
            <div className="relative">
              <select
                value={filters.action_type}
                onChange={(e) => setFilters((f) => ({ ...f, action_type: e.target.value }))}
                className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 pr-7 focus:outline-none focus:ring-1 focus:ring-[#004A99] bg-slate-50 appearance-none"
              >
                {ACTION_TYPES.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
              <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button
            onClick={handleApply}
            className="px-4 py-1.5 text-xs font-medium text-white bg-[#004A99] hover:bg-[#003d80] rounded transition-colors"
          >
            Appliquer
          </button>
          <button
            onClick={handleReset}
            className="px-4 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded transition-colors"
          >
            Réinitialiser
          </button>
        </div>
      </div>

      {/* Stats strip */}
      {!loading && events.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-slate-500">{events.length} événement{events.length > 1 ? 's' : ''} ·</span>
          {Object.entries(stats).map(([key, count]) => (
            <span key={key} className="flex items-center gap-1">
              <ActionBadge actionKey={key} label={`${STAT_LABELS[key] || key}: ${count}`} />
            </span>
          ))}
        </div>
      )}

      {/* Revert feedback */}
      {revertMsg && (
        <div className={`flex items-center gap-2 p-3 text-sm rounded border ${
          revertMsg.type === 'success'
            ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
            : 'text-red-700 bg-red-50 border-red-200'
        }`}>
          <AlertCircle size={14} className="shrink-0" />
          {revertMsg.text}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded">
          <AlertCircle size={14} className="shrink-0" />
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-sm text-slate-400">
            <RefreshCw size={16} className="animate-spin mr-2" />
            Chargement du journal d'audit...
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-16">
            <ScrollText size={32} className="mx-auto text-slate-200 mb-3" />
            <p className="text-sm text-slate-400">Aucun événement trouvé</p>
            {hasActiveFilters && (
              <p className="text-xs text-slate-400 mt-1">Essayez d'élargir vos critères de filtre</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-3 py-2.5 font-medium text-slate-500 whitespace-nowrap">Date / Heure</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-500">Utilisateur</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-500 whitespace-nowrap">Type d'action</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-500">Module</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-500">Référence</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-500 w-px">Détails</th>
                  <th className="px-3 py-2.5 w-20" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {events.map((ev) => {
                  const isExpanded = expandedId === ev.id;
                  const canRevert = ev.log_id && !ev.reverted && ['manual', 'auto', 'direct_edit', 'reimport'].includes(ev.action_key);
                  return (
                    <React.Fragment key={ev.id}>
                      <tr
                        onClick={() => setExpandedId(isExpanded ? null : ev.id)}
                        className={`cursor-pointer transition-colors ${
                          isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50/60'
                        } ${ev.reverted ? 'opacity-60' : ''}`}
                      >
                        <td className="px-3 py-2 text-slate-500 whitespace-nowrap font-mono text-[11px]">
                          {formatTs(ev.timestamp)}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                              <User size={10} className="text-slate-400" />
                            </div>
                            <span className="font-medium text-slate-700">{ev.user}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <ActionBadge actionKey={ev.action_key} label={ev.action_type} />
                            {ev.reverted && (
                              <span className="text-[10px] text-red-500 font-semibold">annulée</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-slate-500">{ev.module}</td>
                        <td className="px-3 py-2 text-slate-700 font-medium whitespace-nowrap">{ev.entity_ref}</td>
                        <td className="px-3 py-2 text-slate-500 max-w-xs">
                          {isExpanded ? (
                            <span className="break-words">{ev.details}</span>
                          ) : (
                            <span className="truncate block max-w-xs" title={ev.details}>{ev.details}</span>
                          )}
                        </td>
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          {canRevert && (
                            <button
                              onClick={() => handleRevert(ev.log_id)}
                              disabled={revertLoading === ev.log_id}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] border border-slate-300 text-slate-600 hover:bg-slate-50 rounded disabled:opacity-50"
                              title="Annuler cette correction"
                            >
                              {revertLoading === ev.log_id
                                ? <RefreshCw size={11} className="animate-spin" />
                                : <RotateCcw size={11} />}
                              Annuler
                            </button>
                          )}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-slate-50/50">
                          <td colSpan={7} className="px-3 py-3 text-xs text-slate-500">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              <div><span className="text-slate-400">ID:</span> <span className="font-mono">{ev.id}</span></div>
                              <div><span className="text-slate-400">Date arrêté:</span> <span className="font-mono">{ev.closing_date || '—'}</span></div>
                              <div><span className="text-slate-400">Fichier:</span> <span className="font-mono">{ev.fichier || '—'}</span></div>
                              <div><span className="text-slate-400">Action:</span> <span className="font-mono">{ev.action_key}</span></div>
                              {ev.poste && <div><span className="text-slate-400">Poste:</span> <span className="font-mono">{ev.poste}</span></div>}
                              {ev.col_name && <div><span className="text-slate-400">Colonne:</span> <span className="font-mono">{ev.col_name}</span></div>}
                              {ev.old_value !== undefined && <div><span className="text-slate-400">Ancienne val.:</span> <span className="font-mono">{ev.old_value}</span></div>}
                              {ev.new_value !== undefined && <div><span className="text-slate-400">Nouvelle val.:</span> <span className="font-mono">{ev.new_value}</span></div>}
                              {ev.rule_code && <div><span className="text-slate-400">Règle:</span> <span className="font-mono">{ev.rule_code}</span></div>}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {events.length > 0 && (
        <p className="text-[11px] text-slate-400 text-center">
          {events.length} événements · Cliquez sur une ligne pour afficher les détails · Annulable si non déjà annulée
        </p>
      )}
    </div>
  );
};

export default AuditLogPage;
